import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Clock, Hash, BookOpen, Pin, ChevronLeft } from 'lucide-react';
import { Input } from '../components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HashChip = ({ tag, active, onClick }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
      active
        ? 'bg-amber-500 text-slate-950 shadow-lg shadow-amber-500/30'
        : 'bg-slate-800/60 text-slate-300 hover:bg-slate-700 border border-slate-700'
    }`}
    data-testid={`hashtag-chip-${tag}`}
  >
    <Hash className="w-3 h-3" />{tag}
  </button>
);

const Knowledge = () => {
  const [articles, setArticles] = useState([]);
  const [hashtags, setHashtags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeHash, setActiveHash] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [aRes, hRes] = await Promise.all([
          axios.get(`${API}/knowledge/articles`),
          axios.get(`${API}/knowledge/hashtags`),
        ]);
        setArticles(aRes.data.articles || []);
        setHashtags(hRes.data.hashtags || []);
      } catch (_e) { /* keep loading state false */ }
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    let list = articles;
    if (activeHash) list = list.filter(a => (a.hashtags || []).includes(activeHash));
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(a => a.title.toLowerCase().includes(q) || (a.excerpt || '').toLowerCase().includes(q) || (a.hashtags || []).some(t => t.includes(q)));
    }
    return list;
  }, [articles, activeHash, search]);

  const [hero, ...rest] = filtered;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="knowledge-page">
      {/* Top nav */}
      <header className="border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition text-sm" data-testid="back-to-home-link">
            <ChevronLeft className="w-4 h-4" />Back to WillKart
          </Link>
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-amber-400" />
            <span className="text-sm font-medium text-slate-300">Knowledge</span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 text-xs font-medium mb-4">
            <BookOpen className="w-3 h-3" />KNOWLEDGE BASE
          </div>
          <h1 className="text-4xl lg:text-5xl font-serif text-slate-100 mb-3" data-testid="knowledge-page-title">Insights on wills, trusts, and estate planning</h1>
          <p className="text-slate-400 max-w-2xl mx-auto">Curated guidance from the WillKart editorial team. Practical, jargon-free articles to help you secure your legacy.</p>
        </div>

        {/* Search + hashtag filters */}
        <div className="grid md:grid-cols-[1fr_auto] gap-4 items-center mb-12 max-w-5xl mx-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search articles..."
              className="pl-10 bg-slate-900/80 border-slate-700 text-slate-100 placeholder:text-slate-500 focus:border-amber-500"
              data-testid="knowledge-search-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <HashChip tag="all" active={!activeHash} onClick={() => setActiveHash(null)} />
            {hashtags.slice(0, 10).map(h => (
              <HashChip key={h.tag} tag={h.tag} active={activeHash === h.tag} onClick={() => setActiveHash(h.tag === activeHash ? null : h.tag)} />
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-24 text-slate-500">Loading articles…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24">
            <BookOpen className="w-12 h-12 text-slate-700 mx-auto mb-3" />
            <p className="text-slate-400">No articles match your filters.</p>
          </div>
        ) : (
          <>
            {/* Hero article (latest pinned or latest published) */}
            {hero && (
              <Link to={`/knowledge/${hero.slug}`} className="block mb-16 group" data-testid={`hero-article-${hero.slug}`}>
                <article className="grid lg:grid-cols-2 gap-8 items-center">
                  <div className="relative aspect-[5/3] rounded-2xl overflow-hidden border border-slate-800 bg-slate-900">
                    {hero.cover_image ? (
                      <img src={hero.cover_image} alt={hero.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-700"><BookOpen className="w-12 h-12" /></div>
                    )}
                    {hero.pinned && (
                      <span className="absolute top-4 left-4 inline-flex items-center gap-1 bg-amber-500 text-slate-950 px-2.5 py-1 rounded-full text-xs font-bold">
                        <Pin className="w-3 h-3" />FEATURED
                      </span>
                    )}
                  </div>
                  <div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {(hero.hashtags || []).slice(0, 3).map(t => <HashChip key={t} tag={t} />)}
                    </div>
                    <h2 className="text-3xl lg:text-4xl font-serif text-slate-100 leading-tight mb-4 group-hover:text-amber-400 transition-colors">{hero.title}</h2>
                    <p className="text-lg text-slate-400 mb-6 line-clamp-3">{hero.excerpt}</p>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span>{hero.author}</span>
                      <span>•</span>
                      <span>{hero.published_at ? new Date(hero.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}</span>
                    </div>
                  </div>
                </article>
              </Link>
            )}

            {/* Grid of remaining articles */}
            {rest.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {rest.map(a => (
                  <Link key={a.id} to={`/knowledge/${a.slug}`} className="group block" data-testid={`article-card-${a.slug}`}>
                    <article>
                      <div className="relative aspect-[4/3] rounded-xl overflow-hidden mb-4 border border-slate-800 bg-slate-900">
                        {a.cover_image ? (
                          <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-700"><BookOpen className="w-8 h-8" /></div>
                        )}
                        {a.pinned && (
                          <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-amber-500 text-slate-950 px-2 py-1 rounded-full text-xs font-bold">
                            <Pin className="w-3 h-3" />FEATURED
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {(a.hashtags || []).slice(0, 2).map(t => <HashChip key={t} tag={t} />)}
                      </div>
                      <h3 className="text-lg font-semibold text-slate-100 mb-2 group-hover:text-amber-400 transition-colors line-clamp-2">{a.title}</h3>
                      <p className="text-sm text-slate-400 line-clamp-2 mb-3">{a.excerpt}</p>
                      <div className="text-xs text-slate-500 flex items-center gap-2">
                        <span>{a.published_at ? new Date(a.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Knowledge;
